import * as React from 'react';
import type { Ind } from '../../../MedicareProAPI/Entities/Ind/Ind';
import type { IndFlowAssignment } from '../../../MedicareProAPI/Entities/Ind/IndFlowAssignment';
import type { UserFlow, FlowBundlePayload } from '../../../MedicareProAPI/Entities/User/UserFlow';
import type { UserFlowStage } from '../../../MedicareProAPI/Entities/User/UserFlowStage';
import { IndFlowAssignmentRepository } from '../../../MedicareProAPI/Repositories/Ind/IndFlowAssignmentRepository';
import { UserFlowStageRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowStageRepository';
import { IndProfileMini } from '../IndProfileMini';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import { UserFlowRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowRepository';
import { SIDETAB_POSITION, SideTab, SIDETAB_SIZE } from '../../Components/SideTab';
import StyledModal from '../StyledModal';
import { FlowStageLane } from './FlowStageLane';
import { IndSearch } from '../Form/Field/IndSearch';

interface FlowKanbanProps {
  user: User;
  userId: number;
  userAccessToken: string;
  operatorName: string;
  userFlow: UserFlow;
  fetchIndsByStage: (
    flowId: number,
    flowStageId: number,
    offset?: number,
    limit?: number,
    sortBy?: string,
    direction?: string,
    criteria?: any
  ) => Promise<StageResponse>;
  usersAgencyId?: number;
  directUplineId?: number;
  absoluteUplineId?: number;
  staffId?: number;
  onUpsert: (payload: FlowBundlePayload) => void | Promise<void>;
  onEdit: (editing: boolean) => void;
}

type StageResponse = {
  individuals: IndFlowAssignment[];
  offset: number;
  limit: number;
  total: number;
};

type StageData = {
  [stageId: number]: {
    individuals: IndFlowAssignment[];
    offset: number;
    total: number;
  };
};

export const FlowKanban = ({
  user,
  userId,
  userAccessToken,
  operatorName,
  userFlow,
  fetchIndsByStage,
  usersAgencyId,
  directUplineId,
  absoluteUplineId,
  staffId,
  onUpsert,
  onEdit,
}: FlowKanbanProps): React.JSX.Element => {
  const [stageData, setStageData] = React.useState<StageData>({});
  const [draggingId, setDraggingId] = React.useState<number | null>(null);
  const [draggedStageId, setDraggedStageId] = React.useState<number | null>(null);
  const [orderedStages, setOrderedStages] = React.useState<UserFlowStage[]>([]);
  const [loadingStages, setLoadingStages] = React.useState<Record<number, boolean>>({});
  const [selectedInd, setSelectedInd] = React.useState<Ind>(null as any);
  const [showSideTab, setShowSideTab] = React.useState<boolean>(false);
  const [size, setSize] = React.useState<SIDETAB_SIZE>(SIDETAB_SIZE.MEDIUM);
  const [flowName, setFlowName] = React.useState<string>(userFlow?.getName?.() ?? '');
  const [bulkSourceStageId, setBulkSourceStageId] = React.useState<number | null>(null);
  const [moveAllModal, setMoveAllModal] = React.useState<boolean>(false);
  const [isMoving, setIsMoving] = React.useState<boolean>(false);
  const [isClosing, setIsClosing] = React.useState<boolean>(false);
  const [selectedStageForMove, setSelectedStageForMove] = React.useState<number | undefined>();
  const [showEmptyStages, setShowEmptyStages] = React.useState(false);
  const [showLeadingEmpty, setShowLeadingEmpty] = React.useState(false);
  const [laneSort, setLaneSort] = React.useState<
    Record<number, { sortBy: 'startAt' | 'indLastname'; dir: 'ASC' | 'DESC' }>
  >({});
  const [showAddToStageModal, setShowAddToStageModal] = React.useState<boolean>(false);
  const [individualSelectedToAdd, setIndSelectedToAdd] = React.useState<Ind>();
  const [isAddingIndToStage, setIsAddingIndToStage] = React.useState<boolean>(false);
  const [stageAddingIndTo, setStageAddingIndTo] = React.useState<number>();
  const [closing, setClosing] = React.useState<Record<number, boolean>>({});

  // helper to open modal and remember source stage
  const openMoveAll = (sourceId: number) => {
    setBulkSourceStageId(sourceId);
    // pick a default target (first other stage) to save a click
    const firstOther = flowStageItems.find((s) => s.getId() !== sourceId)?.getId();
    setSelectedStageForMove(firstOther);
    setMoveAllModal(true);
  };

  const indFlowAssignmentRepository = new IndFlowAssignmentRepository();
  indFlowAssignmentRepository.setAccessToken(userAccessToken);

  const userFlowStageRepository = new UserFlowStageRepository();
  userFlowStageRepository.setAccessToken(userAccessToken);

  const userFlowRepository = new UserFlowRepository();
  userFlowRepository.setAccessToken(userAccessToken);

  /** Always derive the current list of stages from the live userFlow */
  const flowStageItems = React.useMemo(
    () => (userFlow?.getFlowStages?.()?.getItems?.() ?? []) as UserFlowStage[],
    [userFlow]
  );

  /** Signature of IDs (to detect add/remove) */
  const stageIdSignature = React.useMemo(
    () =>
      flowStageItems
        .map((s) => s.getId())
        .sort((a, b) => a - b)
        .join(','),
    [flowStageItems]
  );

  /** Signature of properties (to detect edits/reorders even when IDs don't change) */
  const stagePropSignature = React.useMemo(
    () =>
      flowStageItems
        .map((s) =>
          [s.getId(), s.getName?.(), s.getColor?.(), s.getIcon?.(), s.getSortOrder?.()].join('|')
        )
        .join(','),
    [flowStageItems]
  );

  const handleDrop = (
    assignmentId: number,
    individualId: number,
    fromStageId: number,
    toStageId: number
  ) => {
    if (fromStageId === toStageId) return;

    const sourceStage = stageData[fromStageId];
    const movedAssignment = sourceStage?.individuals.find(
      (a: any) => a.getInd().getIndId() === individualId
    );
    if (!movedAssignment) {
      console.warn('Could not find moved assignment');
      return;
    }

    const updatedAssignment = Object.assign(
      Object.create(Object.getPrototypeOf(movedAssignment)),
      movedAssignment
    );

    updatedAssignment.setStartAt(new Date());

    // Optimistic UI update
    setStageData((prev) => {
      const from = prev[fromStageId] ?? { individuals: [], offset: 0, total: 0 };
      const to = prev[toStageId] ?? { individuals: [], offset: 0, total: 0 };

      const newFromInds = from.individuals.filter(
        (a: any) => a.getInd().getIndId() !== individualId
      );
      const newFromTotal = Math.max((from.total ?? from.individuals.length) - 1, 0);

      const existsInTo = to.individuals.some((a: any) => a.getInd().getIndId() === individualId);
      const newToInds = existsInTo ? to.individuals : [updatedAssignment, ...to.individuals];
      const newToTotal = (to.total ?? to.individuals.length) + (existsInTo ? 0 : 1);

      return {
        ...prev,
        [fromStageId]: { ...from, individuals: newFromInds, total: newFromTotal },
        [toStageId]: { ...to, individuals: newToInds, total: newToTotal },
      };
    });

    // Persist server-side
    indFlowAssignmentRepository
      .moveIndFlowAssignment(individualId, {
        flowId: userFlow.getId(),
        movedBy: operatorName,
        stageId: toStageId,
      })
      .then(() => {})
      .catch((err) => {
        console.error('Move failed, reverting', err);
      });

    console.log(`Move ind#${individualId} to stage#${toStageId}`);
  };

  const handleStageDrop = (targetStageId: number) => {
    if (draggedStageId === null || draggedStageId === targetStageId) return;

    const draggedIndex = orderedStages.findIndex((s) => s.getId() === draggedStageId);
    const targetIndex = orderedStages.findIndex((s) => s.getId() === targetStageId);
    if (draggedIndex < 0 || targetIndex < 0) return;

    const newOrder = [...orderedStages];
    const [moved] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, moved);

    setOrderedStages(newOrder);
    setDraggedStageId(null);

    persistStageOrder(newOrder);
  };

  const persistStageOrder = async (stages: UserFlowStage[]) => {
    const payload = stages.map((stage, index) => ({
      id: stage.getId(),
      sortOrder: index,
    }));
    try {
      await userFlowStageRepository.updateUserFlowStageSortOrders(userFlow.getId(), payload);
    } catch (error) {
      console.error('Error saving sort order', error);
    }
  };

  const handleLoadMore = async (stageId: number) => {
    setLoadingStages((prev) => ({ ...prev, [stageId]: true }));
    try {
      const current = stageData[stageId] ?? { offset: 0, individuals: [], total: 0 };
      const nextOffset = current.offset + 10;
      const response = await fetchIndsByStage(userFlow.getId(), stageId, nextOffset);

      setStageData((prev) => ({
        ...prev,
        [stageId]: {
          individuals: [...(prev[stageId]?.individuals || []), ...response.individuals],
          offset: response.offset,
          total: response.total,
        },
      }));
    } finally {
      setLoadingStages((prev) => ({ ...prev, [stageId]: false }));
    }
  };

  /** Keep orderedStages synced when any stage property changes */
  React.useEffect(() => {
    const next = [...flowStageItems].sort(
      (a, b) => (a.getSortOrder?.() ?? 0) - (b.getSortOrder?.() ?? 0)
    );
    setOrderedStages(next);
  }, [userFlow?.getId?.(), stagePropSignature]);

  /** Keep stageData keys in sync with the lane IDs */
  React.useEffect(() => {
    setStageData((prev) => {
      const next: typeof prev = {};
      const ids = new Set(flowStageItems.map((s) => s.getId()));
      for (const id of ids) {
        next[id] = prev[id] ?? { individuals: [], offset: 0, total: 0 };
      }
      return next;
    });
  }, [stageIdSignature]);

  const cachedStageIds = React.useMemo(
    () => new Set(Object.keys(stageData).map((k) => Number(k))),
    [stageData]
  );

  // remember the last stage id signature we loaded against
  const lastLoadedSigRef = React.useRef<string | null>(null);

  // refs to guard re-entrancy and remember what we last fully hydrated
  const hydratingRef = React.useRef(false);

  const userFlowId = userFlow?.getId?.();

  // hydrate only when: 1) first mount, or 2) the set of stage IDs changes
  React.useEffect(() => {
    if (!userFlowId) return;

    const run = async () => {
      if (hydratingRef.current) return;
      hydratingRef.current = true;

      try {
        // current live ids from flow
        const currentIds = new Set(flowStageItems.map((s) => s.getId()));

        // if first time, or stage id set changed since last full load
        const shouldHydrateAll = lastLoadedSigRef.current === null;
        const idSetChanged = lastLoadedSigRef.current !== stageIdSignature;

        if (shouldHydrateAll || idSetChanged) {
          // figure out which lanes are NEW (no cached data yet)
          const newIds: number[] = [];
          for (const id of currentIds) {
            if (!(id in stageData)) newIds.push(id);
          }

          // load only new lanes; keep existing lanes intact
          if (newIds.length > 0) {
            await Promise.all(
              newIds.map(async (id) => {
                setLoadingStages((prev) => ({ ...prev, [id]: true }));
                try {
                  const resp = await fetchIndsByStage(userFlowId, id, 0);
                  setStageData((prev) => ({
                    ...prev,
                    [id]: {
                      individuals: resp.individuals,
                      offset: resp.offset,
                      total: resp.total,
                    },
                  }));
                } finally {
                  setLoadingStages((prev) => ({ ...prev, [id]: false }));
                }
              })
            );
          }

          // prune removed lanes from cache (no fetch)
          setStageData((prev) => {
            const next: typeof prev = {};
            for (const id of currentIds) {
              next[id] = prev[id] ?? { individuals: [], offset: 0, total: 0 };
            }
            return next;
          });

          lastLoadedSigRef.current = stageIdSignature;
        }

        // update name—no DB call
        setFlowName(userFlow.getName());
      } finally {
        hydratingRef.current = false;
      }
    };

    run();
  }, [userFlowId, stageIdSignature, flowStageItems, userFlow]);

  const refreshStage = React.useCallback(
    async (stageId: number) => {
      setLoadingStages((prev) => ({ ...prev, [stageId]: true }));
      try {
        const resp = await fetchIndsByStage(userFlow.getId(), stageId, 0);
        setStageData((prev) => ({
          ...prev,
          [stageId]: {
            individuals: resp.individuals,
            offset: resp.offset,
            total: resp.total,
          },
        }));
      } finally {
        setLoadingStages((prev) => ({ ...prev, [stageId]: false }));
      }
    },
    [fetchIndsByStage, userFlow]
  );

  const moveAll = async () => {
    if (!bulkSourceStageId || !selectedStageForMove) return;
    if (bulkSourceStageId === selectedStageForMove) return;

    setIsMoving(true);
    try {
      // Single bulk server call
      const payload = {
        sourceStageId: bulkSourceStageId,
        targetStageId: selectedStageForMove,
        movedBy: operatorName,
      };
      await indFlowAssignmentRepository.moveAllInStage(userFlow.getId(), payload);

      // Refresh ONLY the two affected lanes
      await Promise.all([refreshStage(bulkSourceStageId), refreshStage(selectedStageForMove)]);

      setMoveAllModal(false);
    } catch (e) {
      console.error('Move-all failed', e);
    } finally {
      setIsMoving(false);
    }
  };

  const removeAllInStage = async (stageId: number) => {
    const ok = window.confirm('Remove all individuals from this stage?');
    if (!ok) return;

    setLoadingStages((prev) => ({ ...prev, [stageId]: true }));
    try {
      await indFlowAssignmentRepository.removeAllInStage(userFlow.getId(), stageId);
      await refreshStage(stageId);
    } catch (e) {
      console.error('Remove-all failed', e);
    } finally {
      setLoadingStages((prev) => ({ ...prev, [stageId]: false }));
    }
  };

  const closeAll = async (stageId: number) => {
    const ok = window.confirm('Close (archive) all individuals from this stage?');
    if (!ok) return;

    if (!stageId) return;

    setIsClosing(true);
    try {
      // Single bulk server call
      const payload = {
        stageId: stageId,
        movedBy: operatorName,
      };
      await indFlowAssignmentRepository.closeAllInStage(userFlow.getId(), payload);

      await Promise.all([refreshStage(stageId)]);
    } catch (e) {
      console.error('Close-all failed', e);
    } finally {
      setIsClosing(false);
    }
  };

  const isStageEmpty = React.useCallback(
    (stageId: number) => {
      const info = stageData[stageId];
      const total = info ? (info.total ?? info.individuals?.length ?? 0) : 0;
      return total === 0;
    },
    [stageData]
  );

  const emptyCount = React.useMemo(
    () => orderedStages.reduce((acc, s) => acc + (isStageEmpty(s.getId()) ? 1 : 0), 0),
    [orderedStages, isStageEmpty]
  );

  const totalStages = orderedStages.length;
  const allEmpty = totalStages > 0 && emptyCount === totalStages;

  const firstNonEmptyIdx = React.useMemo(() => {
    for (let i = 0; i < orderedStages.length; i++) {
      if (!isStageEmpty(orderedStages[i].getId())) return i;
    }
    return -1;
  }, [orderedStages, isStageEmpty]);

  const shouldHideLeadingEmpties = emptyCount > 2 && !allEmpty && firstNonEmptyIdx > 0;

  const stagesToRender = React.useMemo(() => {
    return orderedStages;
    if (!shouldHideLeadingEmpties || showLeadingEmpty) return orderedStages;
    return orderedStages.filter((stage, idx) => {
      const empty = isStageEmpty(stage.getId());
      // hide only if it's a leading empty (to the left of first non-empty)
      return !(empty && idx < firstNonEmptyIdx);
    });
  }, [orderedStages, shouldHideLeadingEmpties, showLeadingEmpty, firstNonEmptyIdx, isStageEmpty]);

  const handleSearch = async (searchTerm: string, stageId: number) => {
    setLoadingStages((s) => ({ ...s, [stageId]: true }));
    try {
      const resp = await fetchIndsByStage(userFlow.getId(), stageId, 0, 15, 'indLastname', 'ASC', {
        search: searchTerm,
      });
      setStageData((prev) => ({
        ...prev,
        [stageId]: { individuals: resp.individuals, offset: resp.offset, total: resp.total },
      }));
    } finally {
      setLoadingStages((s) => ({ ...s, [stageId]: false }));
    }
  };

  // update + refresh
  const handleChangeSort = async (
    stageId: number,
    sortBy: 'startAt' | 'indLastname',
    dir: 'ASC' | 'DESC'
  ) => {
    setLaneSort((prev) => ({ ...prev, [stageId]: { sortBy, dir } }));

    setLoadingStages((s) => ({ ...s, [stageId]: true }));
    try {
      const { sortBy: sb = 'startAt', dir: sd = 'DESC' } = {
        ...(laneSort[stageId] || {}),
        sortBy,
        dir,
      };
      const resp = await fetchIndsByStage(userFlow.getId(), stageId, 0, 15, sb, sd);
      setStageData((prev) => ({
        ...prev,
        [stageId]: { individuals: resp.individuals, offset: resp.offset, total: resp.total },
      }));
    } finally {
      setLoadingStages((s) => ({ ...s, [stageId]: false }));
    }
  };

  const handleAddIndToStage = (stageId: number) => {
    setShowAddToStageModal(true);
    setStageAddingIndTo(stageId);
  };

  const addIndToStage = (individual: Ind, stageId: number) => {
    setIsAddingIndToStage(true);
    const indId = individual.getIndId();
    const payload = {
      flowId: userFlow.getId(),
      stageId: stageId,
      movedBy: operatorName,
    };
    indFlowAssignmentRepository.createIndFlowAssignment(indId, payload).then((response) => {
      setIsAddingIndToStage(false);
      setIndSelectedToAdd(null);
      setShowAddToStageModal(false);
      refreshStage(stageId);
    });
  };

  const handleCloseOne = async ({ stageId, assignmentId, indId }) => {
    if (!confirm('Close individual in this stage?')) return;

    // optimistic: remove from lane & mark closing
    setClosing((s) => ({ ...s, [assignmentId]: true }));
    setStageData((prev) => {
      const lane = prev[stageId] ?? { individuals: [], offset: 0, total: 0 };
      const next = lane.individuals.filter((a) => a.getId() !== assignmentId);
      const total = Math.max((lane.total ?? next.length) - 1, 0);
      return { ...prev, [stageId]: { ...lane, individuals: next, total } };
    });

    try {
      const payload = {
        movedBy: operatorName,
      };

      await indFlowAssignmentRepository.closeIndFlowAssignment(indId, assignmentId, payload);
    } catch (e) {
      await refreshStage(stageId);
    } finally {
      setClosing((s) => {
        const { [assignmentId]: _, ...rest } = s;
        return rest;
      });
    }
  };

  return (
    <>
      <div>
        <h5>
          <i className="far fa-stream mr-2"></i>
          <span className="cp">{flowName}</span>{' '}
          <i
            className="far fa-pencil text-muted hideEdit ml-2"
            style={{ fontSize: '10pt' }}
            onClick={() => onEdit(true)}
          ></i>
        </h5>
        {/* Toggle appears only when there are 2+ empty stages 
        {shouldHideLeadingEmpties && (
          <div className="p-2 pr-3 d-flex align-items-center" style={{ flex: '0 0 auto' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowLeadingEmpty(v => !v)}
              title={showLeadingEmpty ? 'Hide empty stages' : 'Show empty stages'}
            >
              {showLeadingEmpty
                ? (<><i className="far fa-eye-slash mr-1" /> Hide empty stages</>)
                : (<><i className="far fa-eye mr-1" /> Show empty stages</>)}
            </button>
          </div>
              )} */}
      </div>

      <div className="w-100 overflow-auto" style={{ whiteSpace: 'nowrap' }}>
        <div className="d-flex flex-row align-items-start">
          {flowStageItems.length > 0 &&
            stagesToRender.map((stage) => {
              const stageId = stage.getId();
              const stageInfo = stageData[stageId] || { individuals: [], offset: 0, total: 0 };

              return (
                <FlowStageLane
                  key={stageId}
                  sortBy={laneSort[stageId]?.sortBy ?? 'startAt'}
                  sortDir={laneSort[stageId]?.dir ?? 'DESC'}
                  stage={stage}
                  data={stageInfo}
                  loading={!!loadingStages[stageId]}
                  draggingId={draggingId}
                  onCardDragStart={(p) => setDraggingId(p.individualId)}
                  onCardDragEnd={() => setDraggingId(null)}
                  onLaneDrop={(targetId) => handleStageDrop(targetId)}
                  onCardDropToStage={(p, targetId) =>
                    handleDrop(p.assignmentId, p.individualId, p.currentStageId, targetId)
                  }
                  onLoadMore={handleLoadMore}
                  onOpenMoveAll={openMoveAll}
                  onRemoveAll={removeAllInStage}
                  onCloseAll={closeAll}
                  onChangeSort={handleChangeSort}
                  onOpenInd={(indId) => {
                    setSelectedInd(
                      (
                        stageInfo.individuals.find((a) => a.getInd()?.getIndId() === indId) as any
                      )?.getInd()
                    );
                    setShowSideTab(true);
                  }}
                  onShowAddToStageModalClick={handleAddIndToStage}
                  onCloseOne={handleCloseOne}
                  onSearch={handleSearch}
                />
              );
            })}

          {flowStageItems.length === 0 && <div>Your Flow has no stages.</div>}
        </div>
      </div>

      {/* Mini profile */}
      <SideTab
        size={size}
        position={SIDETAB_POSITION.RIGHT}
        title={''}
        show={showSideTab}
        onClose={() => setShowSideTab(false)}
      >
        {selectedInd && selectedInd.getIndId() && (
          <IndProfileMini
            user={user}
            userId={userId}
            userAccessToken={userAccessToken}
            indId={selectedInd.getIndId()}
            include={[
              'addresses',
              'phones',
              'emails',
              'spouse',
              'spouse',
              'policies',
              'policies.company',
              'policies.plan',
              'flags',
            ]}
            onSizeChange={(e) => setSize(e)}
            usersAgencyId={usersAgencyId}
            directUplineId={directUplineId}
            absoluteUplineId={absoluteUplineId}
            staffId={staffId}
            operatorName={operatorName}
          />
        )}
      </SideTab>

      {/*Move All*/}
      <StyledModal
        size="md"
        title="Move All"
        footerCloseButton={true}
        footerButtons={
          <button onClick={moveAll} className="btn btn-primary" type="button">
            {isMoving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Moving
              </>
            ) : (
              <>Move</>
            )}
          </button>
        }
        show={moveAllModal}
        onClose={() => setMoveAllModal(false)}
      >
        <div className="row pl-3 pr-3">
          <p>Select the stage to move all individuals to</p>
          <select
            className="form-control form-control-sm"
            onChange={(e) => setSelectedStageForMove(parseInt(e.target.value))}
          >
            {flowStageItems.length > 0 &&
              flowStageItems.map((item) => {
                return (
                  item.getId() !== bulkSourceStageId && (
                    <option value={item.getId()}>{item.getName()}</option>
                  )
                );
              })}
          </select>
        </div>
      </StyledModal>

      {/*Add to stage*/}
      <StyledModal
        size="md"
        title="Add to stage"
        footerCloseButton={true}
        footerButtons={
          <button
            onClick={() => addIndToStage(individualSelectedToAdd, stageAddingIndTo)}
            className="btn btn-primary"
            type="button"
          >
            {isAddingIndToStage ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Adding
              </>
            ) : (
              <>Add</>
            )}
          </button>
        }
        show={showAddToStageModal}
        onClose={() => setShowAddToStageModal(false)}
      >
        <div className="row pl-3 pr-3">
          <p>Add an individual to stage</p>
          <IndSearch onChange={setIndSelectedToAdd} value={individualSelectedToAdd} />
          <div className="mt-2">
            Or add in bulk from{' '}
            <a href="https://www.medicareproapp.com/agent/mybook/index">Reporting</a>
          </div>
        </div>
      </StyledModal>
    </>
  );
};
